import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { cerrarSesion, credencialActual, fijarModoDeIdentidad, type ModoDeIdentidad } from '@nucleo/sesion'
import { confirmarSalida } from '@nucleo/trabajoSinGuardar'
import { PantallaAcceso } from '@funcionalidades/acceso/PantallaAcceso'
import { ListaDeMatrices } from '@funcionalidades/matrices/ListaDeMatrices'
import { NuevaMatriz } from '@funcionalidades/matrices/NuevaMatriz'
import { PaginaMatriz } from '@funcionalidades/matrices/PaginaMatriz'
import { Boton } from '@ui/primitivas/Boton'
import { SelectorDeApariencia } from '@ui/primitivas/SelectorDeApariencia'

const ENLACES = [
  { ruta: '/', etiqueta: 'Matrices', descripcion: 'Borradores y revisiones oficiales', icono: 'M4 5h16v14H4zM4 10h16M10 10v9' },
  { ruta: '/matrices/nueva', etiqueta: 'Nueva matriz', descripcion: 'Alcance, tareas, riesgos y medidas', icono: 'M12 5v14M5 12h14' },
] as const

/** Pantalla completa de espera o error antes de conocer la sesión. */
function Estado({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return (
    <main className="sst-ingreso font-base text-cuerpo">
      <section className="sst-ingreso-tarjeta" aria-live="polite">
        <div className="sst-ingreso-marca">
          <img src="/chilesst/chilesst-mark-dark-bg.svg" alt="" width="40" height="40" />
          <span className="sst-marca-texto"><b>Chile<span>SST</span></b><small>Atalaya</small></span>
        </div>
        {error ? <p role="alert" className="sst-ingreso-texto">{children}</p> : <p className="sst-ingreso-texto inline-flex items-center gap-2"><span className="aro-de-espera" aria-hidden="true" />{children}</p>}
        {error ? <button type="button" className="sst-cta" onClick={() => window.location.reload()}>Reintentar</button> : null}
      </section>
    </main>
  )
}

function Shell(props: { modo: ModoDeIdentidad; organizacion: string | null; alSalir: () => void; errorDeSalida: string | null; children: ReactNode }) {
  const navegar = useNavigate()
  const ubicacion = useLocation()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const botonMenu = useRef<HTMLButtonElement>(null)

  useEffect(() => { setMenuAbierto(false) }, [ubicacion.pathname])
  useEffect(() => {
    if (!menuAbierto) return
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return
      setMenuAbierto(false)
      botonMenu.current?.focus()
    }
    document.addEventListener('keydown', alTeclear)
    return () => { document.removeEventListener('keydown', alTeclear) }
  }, [menuAbierto])

  const ir = (ruta: string) => { if (confirmarSalida()) navegar(ruta) }
  const conectado = props.modo === 'chilesst'

  return (
    <div className="sst-app font-base text-cuerpo text-texto">
      <a href="#contenido" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-2 focus:z-aviso focus:rounded-md focus:bg-marca focus:px-4 focus:py-3 focus:font-semibold focus:text-white">
        Saltar al contenido
      </a>
      {menuAbierto ? <div className="sst-velo" aria-hidden="true" onClick={() => setMenuAbierto(false)} /> : null}

      <aside id="menu-lateral" className="sst-lateral" data-abierto={menuAbierto ? 'true' : 'false'} aria-label="Atalaya en ChileSST">
        <Link to="/" className="sst-marca" aria-label="Atalaya, inicio" onClick={(e) => { e.preventDefault(); ir('/') }}>
          <img src="/chilesst/chilesst-mark-dark-bg.svg" alt="" width="34" height="34" />
          <span className="sst-marca-texto"><b>Chile<span>SST</span></b><small>Atalaya</small></span>
        </Link>
        <div className="sst-modulo">
          <strong>Matriz IPER asistida</strong>
          <span>Identificación de peligros y evaluación de riesgos. Atalaya propone, tú validas.</span>
        </div>
        <nav aria-label="Navegación principal" className="sst-nav">
          <p className="sst-nav-titulo" aria-hidden="true">Trabajo</p>
          {ENLACES.map((enlace) => {
            const activo = enlace.ruta === '/' ? ubicacion.pathname === '/' || (ubicacion.pathname.startsWith('/matrices/') && ubicacion.pathname !== '/matrices/nueva') : ubicacion.pathname === enlace.ruta
            return (
              <Link key={enlace.ruta} to={enlace.ruta} className="sst-nav-item" aria-current={activo ? 'page' : undefined} onClick={(e) => { e.preventDefault(); ir(enlace.ruta) }}>
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={enlace.icono} /></svg>
                <span><b>{enlace.etiqueta}</b><small>{enlace.descripcion}</small></span>
              </Link>
            )
          })}
        </nav>
        <div className="sst-lateral-pie">
          <a href="https://chilesst.cl/#modulos" className="sst-enlace-ecosistema" onClick={(e) => { if (!confirmarSalida()) e.preventDefault() }}>
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            Volver a herramientas
          </a>
          {conectado ? <a href="https://chilesst.cl/cuenta" className="sst-enlace-ecosistema sst-solo-movil">Mi cuenta ChileSST</a> : null}
          {props.organizacion ? (
            <div className="sst-sesion">
              <span className="cs-avatar" aria-hidden="true">{props.organizacion.slice(0, 2).toUpperCase()}</span>
              <span className="sst-sesion-datos">
                <b>Organización</b>
                <span title={props.organizacion}>{props.organizacion}</span>
              </span>
            </div>
          ) : null}
          <span className="cs-state cs-state--live">{conectado ? 'Sesión ChileSST activa' : 'Sesión de organización activa'}</span>
        </div>
      </aside>

      <div className="sst-principal">
        <header className="sst-cabecera">
          <div className="sst-cabecera-contexto">
            <button
              ref={botonMenu}
              type="button"
              className="sst-boton-icono sst-solo-movil"
              aria-label={menuAbierto ? 'Cerrar navegación' : 'Abrir navegación'}
              aria-expanded={menuAbierto}
              aria-controls="menu-lateral"
              onClick={() => setMenuAbierto((v) => !v)}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">{menuAbierto ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}</svg>
            </button>
            <img className="sst-solo-movil" src="/chilesst/chilesst-mark.svg" alt="" width="28" height="28" />
            <div className="sst-cabecera-modulo">
              <b>Atalaya</b>
              <span>Matriz IPER asistida</span>
            </div>
          </div>
          <div className="sst-cabecera-acciones">
            <a className="sst-volver" data-volver href={conectado ? 'https://chilesst.cl/cuenta' : 'https://chilesst.cl/#modulos'} aria-label={conectado ? 'Volver a Mi espacio en ChileSST' : 'Volver a ChileSST'} onClick={(evento) => { if (!confirmarSalida()) evento.preventDefault() }}><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6" /></svg><span><span className="sst-volver-largo">{'Volver a '}</span>{conectado ? 'Mi espacio' : 'ChileSST'}</span></a>
            <SelectorDeApariencia />
            <Boton variante="secundario" alPulsar={() => { if (confirmarSalida()) props.alSalir() }}>Cerrar sesión</Boton>
          </div>
        </header>
        {props.errorDeSalida ? <p role="alert" className="mx-4 mt-3 rounded-md bg-critico-fondo p-3 text-critico-texto md:mx-8">{props.errorDeSalida}</p> : null}
        <main id="contenido" tabIndex={-1} className="sst-contenido">{props.children}</main>
        <footer className="sst-pie">
          <span>© {new Date().getFullYear()} ChileSST · Atalaya · Matriz IPER asistida</span>
          <span>Atalaya propone; la validación y la oficialización son siempre de una persona.</span>
        </footer>
      </div>
    </div>
  )
}

export function App() {
  const navegar = useNavigate()
  const ubicacion = useLocation()
  const [modo, setModo] = useState<ModoDeIdentidad | null>(null)
  const [conSesion, setConSesion] = useState(false)
  const [organizacion, setOrganizacion] = useState<string | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)
  const [errorDeSalida, setErrorDeSalida] = useState<string | null>(null)

  useEffect(() => {
    let vigente = true
    const comprobar = async () => {
      try {
        const respuesta = await fetch('/api/sesion', { credentials: 'same-origin', cache: 'no-store' })
        if (!respuesta.ok) throw new Error('No se pudo consultar el modo de ingreso.')
        const datos = await respuesta.json() as { identidad?: string }
        if (datos.identidad !== 'chilesst' && datos.identidad !== 'credenciales') throw new Error('Modo de ingreso inválido.')
        if (!vigente) return
        fijarModoDeIdentidad(datos.identidad)
        setModo(datos.identidad)
        if (datos.identidad === 'credenciales') {
          setConSesion(credencialActual() !== null)
        } else {
          const yo = await fetch('/api/yo', { credentials: 'same-origin', cache: 'no-store' })
          if (!vigente) return
          setConSesion(yo.ok)
          if (yo.ok) {
            const cuerpo = await yo.json().catch(() => null) as { organizacion_id?: unknown } | null
            if (vigente && typeof cuerpo?.organizacion_id === 'string') setOrganizacion(cuerpo.organizacion_id)
          }
        }
      } catch {
        if (vigente) setFallo('No fue posible verificar el acceso a Atalaya. Revisa tu conexión e intenta de nuevo.')
      }
    }
    void comprobar()
    return () => { vigente = false }
  }, [])

  if (fallo) return <Estado error>{fallo}</Estado>
  if (modo === null) return <Estado>Comprobando acceso…</Estado>

  if (!conSesion) {
    return <PantallaAcceso modo={modo} alEntrar={() => setConSesion(true)} />
  }
  const invalidar = () => {
    cerrarSesion()
    setConSesion(false)
    navegar('/')
  }
  const salir = async () => {
    setErrorDeSalida(null)
    if (modo === 'chilesst') {
      try {
        const respuesta = await fetch('/api/sesion/cerrar', { method: 'POST', credentials: 'same-origin' })
        if (!respuesta.ok) throw new Error('No se pudo cerrar la sesión.')
      } catch {
        setErrorDeSalida('No fue posible cerrar la sesión. Intenta nuevamente.')
        return
      }
    }
    invalidar()
  }
  return (
    <Shell modo={modo} organizacion={organizacion} alSalir={() => { void salir() }} errorDeSalida={errorDeSalida}>
      <div key={ubicacion.pathname.split('/').slice(0, 3).join('/')} className="sst-entrada">
        <Routes>
          <Route path="/" element={<ListaDeMatrices alSesionInvalida={invalidar} />} />
          <Route path="/matrices/nueva" element={<NuevaMatriz />} />
          <Route path="/matrices/:id/*" element={<PaginaMatriz alSesionInvalida={invalidar} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Shell>
  )
}
